import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import StoryImages from '../components/StoryImages';

export default function SiteSuccessStories() {
  const { id: siteId } = useParams();

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeAction, setActiveAction] = useState(null);

  // Create form - simple!
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    challenge: '',
    location: '',
    service_type: '',
    emphasis: '',
    model: 'sonnet',
  });
  const [showExtras, setShowExtras] = useState(false);

  // Current story
  const [current, setCurrent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  // Invented details editor
  const [detailEdits, setDetailEdits] = useState({});

  // Style prompt
  const [showStylePrompt, setShowStylePrompt] = useState(false);
  const [stylePrompt, setStylePrompt] = useState({ custom: '', default: '', isCustom: false });

  // Publish
  const [showPublishPanel, setShowPublishPanel] = useState(false);
  const [postTypes, setPostTypes] = useState([]);
  const [selectedPostType, setSelectedPostType] = useState('posts');
  const [cptFields, setCptFields] = useState(null);
  const [metaInputs, setMetaInputs] = useState({});
  const [acfInputs, setAcfInputs] = useState({});

  // Regen
  const [showRegenInput, setShowRegenInput] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState('');

  useEffect(() => {
    loadStories();
    loadPostTypes();
    // eslint-disable-next-line
  }, [siteId]);

  const loadStories = async () => {
    setLoading(true);
    try { setStories(await api.listSuccessStories(siteId)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const loadPostTypes = async () => {
    try {
      const data = await api.listStoryWpPostTypes(siteId);
      const types = (data.post_types || []).filter(t =>
        !['wp_global_styles', 'elementor_library', 'elementor_snippet', 'cfkef-entries'].includes(t.slug)
      );
      setPostTypes(types);
      const candidate = types.find(t => t.is_story_candidate);
      if (candidate) setSelectedPostType(candidate.rest_base);
    } catch (e) { /* silent */ }
  };

  const loadStylePrompt = async () => {
    try {
      const data = await api.getStoryStylePrompt(siteId);
      setStylePrompt({
        custom: data.custom_prompt || '',
        default: data.default_prompt || '',
        isCustom: data.is_custom,
      });
    } catch (e) { setError(e.message); }
  };

  const saveStylePrompt = async () => {
    try {
      await api.updateStoryStylePrompt(siteId, stylePrompt.custom || null);
      setSuccess('סגנון כתיבה נשמר');
      setShowStylePrompt(false);
    } catch (e) { setError(e.message); }
  };

  const handleGenerate = async () => {
    if (!form.challenge || form.challenge.trim().length < 10) {
      setError('תיאור האתגר חייב להיות לפחות 10 תווים');
      return;
    }
    setActiveAction('generating');
    setError(''); setSuccess('');
    try {
      const data = await api.generateSuccessStory({
        site_id: parseInt(siteId),
        challenge: form.challenge,
        location: form.location || null,
        service_type: form.service_type || null,
        emphasis: form.emphasis || null,
        model: form.model,
      });
      setCurrent(data);
      setEditData(data);
      setDetailEdits({});
      setShowCreate(false);
      loadStories();
      const invCount = (data.invented_details || []).length;
      setSuccess(`סיפור נוצר! $${data.generation_cost?.toFixed(4) || '?'} | ${invCount} פרטים דורשים בדיקה`);
    } catch (e) { setError('שגיאה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const handleView = async (id) => {
    try {
      const data = await api.getSuccessStory(id);
      setCurrent(data);
      setEditData(data);
      setDetailEdits({});
      setEditMode(false);
      setShowPublishPanel(false);
      setShowRegenInput(false);
    } catch (e) { setError(e.message); }
  };

  const handleReplaceDetail = async (inText, newValue) => {
    if (!current || !newValue || newValue === inText) return;
    setActiveAction('replacing');
    try {
      const updated = await api.replaceStoryDetail(current.id, inText, newValue);
      setCurrent(updated);
      setEditData(updated);
      setDetailEdits(prev => {
        const next = { ...prev };
        delete next[inText];
        return next;
      });
      setSuccess('פרט הוחלף בכל הטקסט');
    } catch (e) { setError('שגיאה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const handleSaveEdit = async () => {
    if (!current) return;
    setActiveAction('saving');
    try {
      const updated = await api.updateSuccessStory(current.id, {
        title: editData.title,
        slug: editData.slug,
        content: editData.content,
        meta_title: editData.meta_title,
        meta_description: editData.meta_description,
      });
      setCurrent(updated);
      setEditMode(false);
      setSuccess('נשמר');
    } catch (e) { setError(e.message); }
    finally { setActiveAction(null); }
  };

  const handleRegenerate = async () => {
    if (!current) return;
    setActiveAction('regenerating');
    try {
      const data = await api.regenerateSuccessStory(current.id, {
        additional_instructions: regenInstructions || null,
      });
      setCurrent(data);
      setEditData(data);
      setShowRegenInput(false);
      setRegenInstructions('');
      setSuccess('סיפור חודש! $' + (data.generation_cost?.toFixed(4) || '?'));
    } catch (e) { setError(e.message); }
    finally { setActiveAction(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('למחוק?')) return;
    try {
      await api.deleteSuccessStory(id);
      if (current?.id === id) setCurrent(null);
      loadStories();
    } catch (e) { setError(e.message); }
  };

  const handleSelectPostType = async (restBase) => {
    setSelectedPostType(restBase);
    setCptFields(null); setMetaInputs({}); setAcfInputs({});
    if (restBase && restBase !== 'posts' && restBase !== 'pages') {
      try {
        setCptFields(await api.inspectStoryCptFields(siteId, restBase));
      } catch (e) { /* silent */ }
    }
  };

  const handlePublish = async () => {
    if (!current) return;
    setActiveAction('publishing'); setError('');
    try {
      const metaFields = {};
      if (Object.keys(metaInputs).length) Object.assign(metaFields, metaInputs);
      if (Object.keys(acfInputs).length) metaFields.acf = acfInputs;
      const result = await api.publishStoryToWp(current.id, {
        wp_post_type: selectedPostType,
        wp_meta_fields: Object.keys(metaFields).length ? metaFields : null,
      });
      setCurrent(c => ({ ...c, ...result, status: 'wp_draft' }));
      setSuccess('הועלה ל-' + result.wp_post_type);
      setShowPublishPanel(false);
      loadStories();
    } catch (e) { setError('שגיאה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const st = {
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12 },
    input: { width: '100%', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' },
    textarea: { width: '100%', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', lineHeight: 1.6, minHeight: 80, resize: 'vertical' },
    label: { display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 500 },
    btn: (primary, disabled) => ({
      padding: '8px 14px',
      borderRadius: 'var(--radius-sm)',
      border: primary ? 'none' : '1px solid var(--border)',
      background: primary ? 'var(--accent)' : 'var(--surface-hover)',
      color: primary ? '#fff' : 'var(--text)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13, fontWeight: 500,
      opacity: disabled ? 0.6 : 1,
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }),
    chip: (active) => ({
      padding: '4px 10px', borderRadius: 999,
      border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
      background: active ? 'var(--accent-soft)' : 'var(--bg)',
      color: active ? 'var(--accent)' : 'var(--text-secondary)',
      fontSize: 12, cursor: 'pointer',
    }),
  };

  const isActionActive = !!activeAction;
  const unreplacedDetails = (current?.invented_details || []).filter(d => !d.replaced);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>סיפורי לקוחות</div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 2 }}>
            כתוב אתגר - Claude ירחיב לסיפור שלם. פרטים שהומצאו יסומנו לעריכה.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={st.btn(false)} onClick={() => { setShowStylePrompt(true); loadStylePrompt(); }}>
            ⚙ סגנון כתיבה
          </button>
          <button style={st.btn(true)} onClick={() => setShowCreate(v => !v)} disabled={isActionActive}>
            {showCreate ? '✕ ביטול' : '+ סיפור חדש'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius)', padding: '10px 14px', color: 'var(--red)', fontSize: 13, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
          <span>{error}</span><span onClick={() => setError('')} style={{ cursor: 'pointer' }}>✕</span>
        </div>
      )}
      {success && (
        <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius)', padding: '10px 14px', color: 'var(--green)', fontSize: 13, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
          <span>{success}</span><span onClick={() => setSuccess('')} style={{ cursor: 'pointer' }}>✕</span>
        </div>
      )}

      {/* Style prompt modal */}
      {showStylePrompt && (
        <div style={{ ...st.card, background: 'var(--surface-hover)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>סגנון כתיבה - ספציפי לאתר</div>
            <button style={st.btn(false)} onClick={() => setShowStylePrompt(false)}>✕</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            אם ריק - סגנון ברירת מחדל. ניתן להעתיק את ברירת המחדל ולערוך.
          </div>
          <textarea style={{ ...st.textarea, minHeight: 300, fontFamily: 'monospace', fontSize: 12 }}
            value={stylePrompt.custom}
            onChange={e => setStylePrompt(s => ({ ...s, custom: e.target.value }))}
            placeholder={stylePrompt.default} dir="rtl" />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={st.btn(true)} onClick={saveStylePrompt}>💾 שמור</button>
            <button style={st.btn(false)} onClick={() => setStylePrompt(s => ({ ...s, custom: '' }))}>↺ ברירת מחדל</button>
            <button style={st.btn(false)} onClick={() => setStylePrompt(s => ({ ...s, custom: s.default }))}>📋 העתק לעריכה</button>
          </div>
        </div>
      )}

      {/* Create form - simple */}
      {showCreate && (
        <div style={st.card}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>
            אפיון סיפור - כתוב בחופשיות
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={st.label}>
              האתגר / הסיפור שלך *
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginRight: 6 }}>
                - ככל שתכתוב יותר פרטים אמיתיים, כך פחות Claude ימציא
              </span>
            </div>
            <textarea style={{ ...st.textarea, minHeight: 150 }} dir="rtl"
              placeholder={'דוגמה: "חצר בצורת L בעייתית ברחובות. הלקוחות רצו גינה גדולה אבל הצורה הפכה את זה למסובך. חילקנו ל-3 אזורים עם עצי פרי ודשא סינטטי והתוצאה הייתה מעולה."\n\nאו באופן מינימלי: "פתרון לבעיית ניקוז בחצר"'}
              value={form.challenge}
              onChange={e => setForm(f => ({ ...f, challenge: e.target.value }))} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {form.challenge.length} תווים {form.challenge.length < 10 && '(מינימום 10)'}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <button
              style={{ ...st.btn(false), padding: '6px 10px', fontSize: 12 }}
              onClick={() => setShowExtras(v => !v)}
            >
              {showExtras ? '▼' : '▶'} פרמטרים נוספים (אופציונלי)
            </button>
            {showExtras && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                <div>
                  <div style={st.label}>מיקום (אם ידוע)</div>
                  <input style={st.input} placeholder="רחובות, הוד השרון..." dir="rtl"
                    value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
                </div>
                <div>
                  <div style={st.label}>סוג שירות (אם רלוונטי)</div>
                  <input style={st.input} placeholder="עיצוב גינה, דשא סינטטי..." dir="rtl"
                    value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={st.label}>דגשים מיוחדים ל-Claude</div>
                  <textarea style={{ ...st.textarea, minHeight: 50 }} dir="rtl"
                    placeholder="למשל: הדגש חיסכון במים, כלול ציטוט של לקוח..."
                    value={form.emphasis} onChange={e => setForm(f => ({ ...f, emphasis: e.target.value }))} />
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            <span style={{ ...st.chip(form.model === 'sonnet'), padding: '6px 14px' }} onClick={() => setForm(f => ({ ...f, model: 'sonnet' }))}>✨ Sonnet</span>
            <span style={{ ...st.chip(form.model === 'opus'), padding: '6px 14px' }} onClick={() => setForm(f => ({ ...f, model: 'opus' }))}>👑 Opus</span>
            <span style={{ ...st.chip(form.model === 'haiku'), padding: '6px 14px' }} onClick={() => setForm(f => ({ ...f, model: 'haiku' }))}>⚡ Haiku</span>
          </div>

          <button style={st.btn(true, isActionActive)} onClick={handleGenerate} disabled={isActionActive}>
            {activeAction === 'generating' ? '⏳ יוצר סיפור... (30-60 שניות)' : '✨ צור סיפור'}
          </button>
        </div>
      )}

      {/* Stories + current */}
      <div style={{ display: 'grid', gridTemplateColumns: current ? '280px 1fr' : '1fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--text-secondary)' }}>
            סיפורים ({stories.length})
          </div>
          {loading && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>טוען...</div>}
          {!loading && stories.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 10 }}>אין עדיין</div>
          )}
          {stories.map(s => (
            <div key={s.id} onClick={() => handleView(s.id)}
              style={{ ...st.card, padding: 12, cursor: 'pointer',
                border: current?.id === s.id ? '1px solid var(--accent)' : '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{s.title || 'ללא כותרת'}</div>
              {s.client_names && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  👤 {s.client_names}{s.location && ' · ' + s.location}
                </div>
              )}
              <div style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4,
                background: s.status === 'wp_draft' ? 'var(--green-glow)' : 'var(--surface-hover)',
                color: s.status === 'wp_draft' ? 'var(--green)' : 'var(--text-muted)',
                display: 'inline-block', marginTop: 4 }}>
                {s.status === 'wp_draft' ? 'ב-WP' : 'טיוטה'}
              </div>
            </div>
          ))}
        </div>

        {current && (
          <div>
            {/* Header + actions */}
            <div style={st.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  {editMode ? (
                    <input style={{ ...st.input, fontSize: 18, fontWeight: 600 }} value={editData.title || ''}
                      onChange={e => setEditData(d => ({ ...d, title: e.target.value }))} />
                  ) : (
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{current.title || 'ללא כותרת'}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {editMode ? (
                    <>
                      <button style={st.btn(true, isActionActive)} onClick={handleSaveEdit} disabled={isActionActive}>💾 שמור</button>
                      <button style={st.btn(false)} onClick={() => { setEditMode(false); setEditData(current); }}>ביטול</button>
                    </>
                  ) : (
                    <>
                      <button style={st.btn(false)} onClick={() => setEditMode(true)}>✏ ערוך HTML</button>
                      <button style={st.btn(false)} onClick={() => setShowRegenInput(v => !v)}>🔄 צור מחדש</button>
                      <button style={st.btn(true)} onClick={() => setShowPublishPanel(v => !v)}>📤 העלה ל-WP</button>
                      <button style={{ ...st.btn(false), color: 'var(--red)' }} onClick={() => handleDelete(current.id)}>🗑</button>
                    </>
                  )}
                </div>
              </div>
              {current.wp_post_url && (
                <a href={current.wp_post_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontSize: 12 }}>
                  פתח ב-WordPress →
                </a>
              )}
            </div>

            {/* Invented details warning + editor */}
            {unreplacedDetails.length > 0 && (
              <div style={{ ...st.card, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#F59E0B', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ⚠ Claude המציא {unreplacedDetails.length} פרטים - החלף לפרטים האמיתיים לפני פרסום
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
                  שנה כל פרט ולחץ Enter - זה יחליף אוטומטית בכל הטקסט.
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {unreplacedDetails.map((d, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 100px 1fr auto', gap: 8, alignItems: 'center', fontSize: 12 }}>
                      <div style={{ color: 'var(--text-muted)' }}>{d.description || d.type}</div>
                      <div style={{ color: 'var(--text-secondary)', padding: '6px 8px', background: 'var(--bg)', borderRadius: 4, direction: 'rtl' }}>
                        {d.in_text}
                      </div>
                      <div style={{ color: 'var(--text-muted)', textAlign: 'center' }}>→</div>
                      <input
                        style={{ ...st.input, fontSize: 12, padding: '6px 10px' }}
                        placeholder="פרט אמיתי"
                        value={detailEdits[d.in_text] || ''}
                        onChange={e => setDetailEdits(prev => ({ ...prev, [d.in_text]: e.target.value }))}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && detailEdits[d.in_text]) {
                            handleReplaceDetail(d.in_text, detailEdits[d.in_text]);
                          }
                        }}
                        dir="rtl"
                      />
                      <button
                        style={{ ...st.btn(false), padding: '4px 10px', fontSize: 11 }}
                        onClick={() => handleReplaceDetail(d.in_text, detailEdits[d.in_text])}
                        disabled={!detailEdits[d.in_text] || activeAction === 'replacing'}
                      >
                        ✓
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {unreplacedDetails.length === 0 && current.invented_details?.length > 0 && (
              <div style={{ ...st.card, background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', padding: 10, fontSize: 12, color: 'var(--green)' }}>
                ✓ כל הפרטים המומצאים הוחלפו
              </div>
            )}

            {/* Regen */}
            {showRegenInput && (
              <div style={st.card}>
                <div style={st.label}>הנחיות ליצירה מחדש</div>
                <textarea style={st.textarea} dir="rtl"
                  placeholder='"קצר יותר", "הדגש חיסכון", "שנה פתיחה"'
                  value={regenInstructions} onChange={e => setRegenInstructions(e.target.value)} />
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button style={st.btn(true, isActionActive)} onClick={handleRegenerate} disabled={isActionActive}>
                    {activeAction === 'regenerating' ? '⏳ יוצר...' : '🔄 צור מחדש'}
                  </button>
                  <button style={st.btn(false)} onClick={() => setShowRegenInput(false)}>ביטול</button>
                </div>
              </div>
            )}

            {/* Publish */}
            {showPublishPanel && (
              <div style={st.card}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>העלאה ל-WordPress</div>
                {unreplacedDetails.length > 0 && (
                  <div style={{ fontSize: 12, color: '#F59E0B', marginBottom: 10 }}>
                    ⚠ יש עדיין {unreplacedDetails.length} פרטים מומצאים. מומלץ להחליפם קודם.
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <div style={st.label}>סוג פוסט</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {postTypes.map(t => (
                      <span key={t.slug}
                        style={{ ...st.chip(selectedPostType === t.rest_base),
                          borderColor: t.is_story_candidate && selectedPostType !== t.rest_base ? 'var(--green)' : undefined,
                          color: t.is_story_candidate && selectedPostType !== t.rest_base ? 'var(--green)' : undefined }}
                        onClick={() => handleSelectPostType(t.rest_base)}>
                        {t.is_story_candidate ? '★ ' : ''}{t.name}
                      </span>
                    ))}
                  </div>
                </div>
                {cptFields && cptFields.meta_keys?.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={st.label}>שדות meta</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {cptFields.meta_keys.slice(0, 10).map(key => (
                        <div key={key}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>{key}</div>
                          <input style={{ ...st.input, fontSize: 12 }}
                            value={metaInputs[key] || ''}
                            onChange={e => setMetaInputs(m => ({ ...m, [key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cptFields && cptFields.acf_keys?.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={st.label}>שדות ACF</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {cptFields.acf_keys.slice(0, 10).map(key => (
                        <div key={key}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>{key}</div>
                          <input style={{ ...st.input, fontSize: 12 }}
                            value={acfInputs[key] || ''}
                            onChange={e => setAcfInputs(a => ({ ...a, [key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={st.btn(true, isActionActive)} onClick={handlePublish} disabled={isActionActive}>
                    {activeAction === 'publishing' ? '⏳ מעלה...' : '📤 העלה'}
                  </button>
                  <button style={st.btn(false)} onClick={() => setShowPublishPanel(false)}>ביטול</button>
                </div>
              </div>
            )}

            {/* Google preview */}
            <div style={{ ...st.card, background: '#0a0a0a' }}>
              <div style={{ fontSize: 10, color: '#70757a', marginBottom: 5 }}>Google Preview</div>
              {editMode ? (
                <>
                  <input value={editData.meta_title || ''}
                    onChange={e => setEditData(d => ({ ...d, meta_title: e.target.value }))}
                    style={{ ...st.input, color: '#8ab4f8', fontSize: 17, background: 'transparent', border: '1px solid #333', marginBottom: 5 }} />
                  <input value={editData.meta_description || ''}
                    onChange={e => setEditData(d => ({ ...d, meta_description: e.target.value }))}
                    style={{ ...st.input, color: '#bdc1c6', fontSize: 13, background: 'transparent', border: '1px solid #333' }} />
                </>
              ) : (
                <>
                  <div style={{ color: '#8ab4f8', fontSize: 17, marginBottom: 3, lineHeight: 1.3 }}>{current.meta_title}</div>
                  <div style={{ color: '#bdc1c6', fontSize: 13, lineHeight: 1.5 }}>{current.meta_description}</div>
                </>
              )}
              <div style={{ fontSize: 10, color: '#555', marginTop: 6 }}>
                {(editMode ? editData.meta_title : current.meta_title || '').length}/60 · {(editMode ? editData.meta_description : current.meta_description || '').length}/155 · {current.slug}
              </div>
            </div>

            {/* Content */}
            <div style={st.card}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>תוכן</div>
              {editMode ? (
                <textarea style={{ ...st.textarea, minHeight: 400, fontFamily: 'monospace', fontSize: 12 }}
                  value={editData.content || ''}
                  onChange={e => setEditData(d => ({ ...d, content: e.target.value }))} />
              ) : (
                <div style={{ fontSize: 15, lineHeight: 1.8, direction: 'rtl' }}
                  dangerouslySetInnerHTML={{ __html: current.content || '' }} />
              )}
            </div>

            {/* Challenge input collapsed */}
            <details style={{ ...st.card, cursor: 'pointer' }}>
              <summary style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-secondary)' }}>
                האתגר המקורי
              </summary>
              <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                {current.challenge_input || current.additional_notes || ''}
              </div>
            </details>

            {/* Images */}
            {!editMode && (
              <div style={st.card}>
                <StoryImages storyId={current.id} initialImages={current.images_data}
                  onRefresh={() => handleView(current.id)} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
